// Per-history-entry scroll memory for content articles. Keyed by React
// Router's location.key, which is stable across POP returns and reloads (it
// lives in history.state), with the pathname stored alongside as a guard:
// the initial entry of a fresh tab always gets the key "default", so two
// different cold entries in one tab would otherwise collide.
//
// Held in memory and persisted to sessionStorage on pagehide, so a reload
// comes back to the same reading position without paying a synchronous
// storage write on every scroll event.

const STORAGE_KEY = "kai:scrollMemory";
const MAX_ENTRIES = 100;

type Entry = {pathname: string; top: number};

function loadPersisted(): [string, Entry][] {
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        const parsed: unknown = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? (parsed as [string, Entry][]) : [];
    } catch {
        return [];
    }
}

const memory = new Map<string, Entry>(loadPersisted());

function persist() {
    try {
        sessionStorage.setItem(
            STORAGE_KEY,
            JSON.stringify([...memory.entries()])
        );
    } catch {
        // Best effort — losing scroll memory only costs the restore.
    }
}

if (typeof window !== "undefined") {
    window.addEventListener("pagehide", persist);
}

export function rememberScroll(key: string, pathname: string, top: number) {
    // Re-insert so Map iteration order approximates recency for eviction.
    memory.delete(key);
    memory.set(key, {pathname, top});
    while (memory.size > MAX_ENTRIES) {
        const oldest = memory.keys().next().value;
        if (oldest === undefined) break;
        memory.delete(oldest);
    }
}

export function recallScroll(
    key: string,
    pathname: string
): number | undefined {
    const entry = memory.get(key);
    return entry && entry.pathname === pathname ? entry.top : undefined;
}
